import type { Game, Prediction, FixtureView } from './types'
import type { SimGameweekState } from './gameweekSimulation'
import type { LiveFixture } from './api/football'
import { isInPlay } from './components/gameweekGrid/liveState'

interface BuildFixtureViewsArgs {
  games: Game[]
  predictions: Record<string, Prediction>
  /** Ground-truth results for these games, regardless of reveal state. */
  actualResults: Record<string, { homeScore: number; awayScore: number }>
  /** True once this gameweek's results are visible (dev toggle, or a
   * finalized past gameweek). */
  resultsRevealed: boolean
  /** Only set for the gameweek currently mid dev-tool simulation. */
  simState: SimGameweekState | null
  /**
   * In-play fixtures from the last `/api/live` poll.
   *
   * The page had no access to this at all, so a real match in progress rendered as `upcoming` —
   * no score, no minute, no prediction line, and no timeline to open until it settled. Same
   * precedence rule the game cards use: the snapshot wins wherever it has an opinion,
   * including "not in play", and the mock sim is the fallback for fixtures it says nothing about.
   */
  liveFixtures?: LiveFixture[]
}

/**
 * Builds the shared per-fixture view (status, live/final score, prediction)
 * a gameweek's games are rendered from — the same status rule GameweekGrid
 * uses (`resultsRevealed || simState.statuses[id] === 'final'`), so the
 * Fixtures page can never show a different state for the same fixture.
 * 
 */
export function buildFixtureViews({
  games,
  predictions,
  actualResults,
  resultsRevealed,
  simState,
  liveFixtures,
}: BuildFixtureViewsArgs): FixtureView[] {
  const snapshot = new Map((liveFixtures ?? []).map((fixture) => [fixture.id, fixture]))

  return games.map((game) => {
    const simStatus = simState?.statuses[game.id]
    const prediction = predictions[game.id]
    const live = snapshot.get(game.id)

    // The server's word first. A fixture it reports as in play is in play, whatever the reveal
    // toggle or a mock sim thinks; one it reports as anything else falls through to the rules
    // below, so a finished match still resolves to its final score.
    if (live && isInPlay(live.status)) {
      return {
        ...game,
        status: 'live',
        liveStatus: live.status as 'live' | 'halftime',
        liveMinute: live.minute,
        liveScore:
          live.homeScore !== null && live.awayScore !== null
            ? { homeScore: live.homeScore, awayScore: live.awayScore }
            : null,
        finalScore: null,
        prediction,
      }
    }

    if (!live && simStatus === 'live') {
      return {
        ...game,
        status: 'live',
        // The mock sim has no provider status and no ticking clock behind it, so its minute is
        // rendered as the plain number it has always been.
        liveStatus: null,
        liveMinute: simState?.liveMinutes[game.id] ?? null,
        liveScore: simState?.liveResults[game.id] ?? null,
        finalScore: null,
        prediction,
      }
    }

    const isRevealed = resultsRevealed || simStatus === 'final'
    if (isRevealed && actualResults[game.id]) {
      return {
        ...game,
        status: 'final',
        liveStatus: null,
        liveMinute: null,
        liveScore: null,
        finalScore: actualResults[game.id],
        prediction,
      }
    }

    return {
      ...game,
      status: 'upcoming',
      liveStatus: null,
      liveMinute: null,
      liveScore: null,
      finalScore: null,
      prediction,
    }
  })
}
